import React from 'react';
import { TablePaginationConfig } from 'antd';

import {
  ConfluenceSourceParametersLoadingMode,
  ConfluenceSourceParametersSettings,
  IngestionSourceModelOutput,
  IngestionSourceParametersType,
  WebsiteSourceParametersReceivingMode,
  WebsiteSourceParametersSettings,
} from '../../../../../../gamma-api';
import { getTranslateFunc } from '../../../../../utils/stringUtil';
import IbTypography from '../../../../../components/common/IbTypography';
import FilterableDebounceInput, {
  FilterableDebounceInputParam,
  IFilterableDebounceInputValue,
} from '../../../../../components/common/FilterableDebounceInput';
import ResourceTable, { IResourceTableItem, ResourceTableField } from '../ResourceTable';

import './index.less';

import { FOOTER_CLASS_NAME, HEADER_CLASS_NAME, MAIN_CLASS_NAME, ROLLING_CLASS_NAME } from './const';

export interface ISourcePreviewingProps {
  sourceLoading: boolean;
  pageLoading: boolean;
  source: IngestionSourceModelOutput;
  resources: IResourceTableItem[];
  resourceFilter?: IFilterableDebounceInputValue;
  resourcePagination: TablePaginationConfig;
  onResourceFilterChange?: (value: IFilterableDebounceInputValue) => void;
  onResourcePaginationChange?: (page: number) => void;
}

const SourcePreviewing: React.FC<ISourcePreviewingProps> = ({
  sourceLoading,
  pageLoading,
  source,
  resources,
  resourceFilter,
  resourcePagination,
  onResourceFilterChange,
  onResourcePaginationChange,
}) => {
  const translate = getTranslateFunc('Source previewing:');

  const rolling = sourceLoading || !!source.versions.rolling;

  const classes = [MAIN_CLASS_NAME];
  if (rolling) {
    classes.push(ROLLING_CLASS_NAME);
  }

  const renderFooterTitle = () => {
    const parameters = source.versions.draft.source.parameters;

    if (
      parameters.type === IngestionSourceParametersType.WebsiteUrls ||
      (parameters.type === IngestionSourceParametersType.Website &&
        (parameters.settings as WebsiteSourceParametersSettings).receiving_mode ===
          WebsiteSourceParametersReceivingMode.Manual)
    ) {
      return translate('Footer: Title: Website: Manual');
    }

    if (
      parameters.type === IngestionSourceParametersType.WebsiteCrawler ||
      (parameters.type === IngestionSourceParametersType.Website &&
        (parameters.settings as WebsiteSourceParametersSettings).receiving_mode ===
          WebsiteSourceParametersReceivingMode.Crawler)
    ) {
      return translate('Footer: Title: Website: Crawler');
    }

    if (parameters.type === IngestionSourceParametersType.File) {
      return translate('Footer: Title: File');
    }

    if (parameters.type === IngestionSourceParametersType.Elma365) {
      return translate('Footer: Title: ELMA365');
    }

    if (
      parameters.type === IngestionSourceParametersType.Confluence &&
      (parameters.settings as ConfluenceSourceParametersSettings).loading_mode ===
        ConfluenceSourceParametersLoadingMode.Pages
    ) {
      return translate('Footer: Title: Confluence: Pages');
    }

    if (
      parameters.type === IngestionSourceParametersType.Confluence &&
      (parameters.settings as ConfluenceSourceParametersSettings).loading_mode ===
        ConfluenceSourceParametersLoadingMode.Space
    ) {
      return translate('Footer: Title: Confluence: Space');
    }

    return translate('Footer: Title: Source');
  };

  return (
    <div className={classes.join(' ')}>
      <IbTypography className={HEADER_CLASS_NAME}>
        <IbTypography.Paragraph strong>
          {translate('Header: Title', { value: rolling ? '?' : resourcePagination.total ?? resources.length })}
        </IbTypography.Paragraph>
      </IbTypography>
      <FilterableDebounceInput
        disabled={rolling}
        params={[FilterableDebounceInputParam.Action]}
        placeholder={translate(`Search: Placeholder: ${source.versions.draft.source.parameters.type}`)}
        value={resourceFilter}
        onChange={onResourceFilterChange}
      />
      <ResourceTable
        fields={[ResourceTableField.Action, ResourceTableField.Name, ResourceTableField.Url]}
        items={resources}
        pageLoading={pageLoading}
        pagination={resourcePagination}
        searchText={resourceFilter?.searchText}
        sourceLoading={rolling}
        onPaginationChange={onResourcePaginationChange}
      />
      {rolling && (
        <IbTypography className={FOOTER_CLASS_NAME}>
          <IbTypography.Paragraph light>{renderFooterTitle()}</IbTypography.Paragraph>
          <IbTypography.Paragraph light>{translate('Footer: Subtitle: 1')}</IbTypography.Paragraph>
          <IbTypography.Paragraph light>{translate('Footer: Subtitle: 2')}</IbTypography.Paragraph>
        </IbTypography>
      )}
    </div>
  );
};

export default SourcePreviewing;
